<script setup lang="ts">
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import { CheckIcon, ChevronRightIcon } from '@heroicons/vue/solid';
import { ref } from 'vue';
import DropZone from '~/components/DropZone.vue';
import { getApiRoot } from '~/utilities/api';
import { getUrlForNft } from '~/utilities/url-slugs';

const open = ref(false);
const { $axios } = useNuxtApp();

const route = useRoute();
const nftId = route.query?.nft;
const side = parseInt(route.query?.side);

const { data: proposedNft } = await useFetch<any>(`${getApiRoot()}/nfts/${nftId}`);

const dropzoneFile = ref('');
const uploadSuccessful = ref(false);
const uploadError = ref(undefined);
const validationError = ref<string | undefined>(undefined);
const offer = ref(undefined);
const nft = ref(undefined);

const drop = async (e) => {
  dropzoneFile.value = e.dataTransfer.files[0];
  await validateOfferFile(dropzoneFile.value);
};
const selectedFile = async () => {
  dropzoneFile.value = (document.querySelector('#dropzoneFile') as any).files[0];

  await validateOfferFile(dropzoneFile.value);
};

const reset = (resetFile = true) => {
  if (resetFile) {
    dropzoneFile.value = undefined;
  }
  uploadSuccessful.value = false;
  validationError.value = undefined;
  uploadError.value = undefined;
  offer.value = undefined;
  nft.value = undefined;
  open.value = false;
};

const validateOfferFile = async (file) => {
  reset(false);

  const formData = new FormData();
  formData.set('offer_file', file);
  try {
    const response = await $axios({
      method: 'post',
      url: `${getApiRoot()}/validate_offer_file`,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    offer.value = response?.data?.offer;
    nft.value = response?.data?.nft;
    validationError.value = response?.data?.is_valid ? undefined : 'This offer is no longer valid.';
  } catch (e) {
    validationError.value = e.response?.data?.detail || e.message;
  }
};

const uploadOfferFile = async (file) => {
  const formData = new FormData();
  formData.set('offer_file', file);
  try {
    useTrackEvent('offer_upload');
    await $axios({
      method: 'post',
      url: `${getApiRoot()}/offer_file`,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    uploadSuccessful.value = true;
    open.value = true;
  } catch (e) {
    console.log(e);

    uploadError.value = e.response?.data?.detail || e.message;
  }
};

const validateOffer = async (o: string) => {
  reset(false);

  try {
    const response = await $axios({
      method: 'post',
      url: `${getApiRoot()}/validate_offer`,
      data: { offer: o },
    });
    offer.value = response?.data?.offer;
    nft.value = response?.data?.nft;
    validationError.value = response?.data?.is_valid ? undefined : 'This offer is no longer valid.';
  } catch (e) {
    validationError.value = e.response?.data?.detail || e.message;
  }
};

const uploadOffer = async (o) => {
  try {
    useTrackEvent('offer_upload');
    await $axios({
      method: 'post',
      url: `${getApiRoot()}/offer`,
      data: { offer: o },
    });
    uploadSuccessful.value = true;
    open.value = true;
  } catch (e) {
    console.log(e);

    uploadError.value = e.response?.data?.detail || e.message;
  }
};

const validateAndUploadOffer = async (o: string) => {
  await validateOffer(o);
  if (offer.value) {
    console.log('uploading offer');
    await uploadOffer(o);
  }
};
</script>
<template>
  <Container>
    <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
      <div
        class="relative max-w-md flex-grow overflow-hidden rounded-lg pb-4 pt-5 text-left sm:my-8 lg:w-full lg:max-w-4xl"
      >
        <div class="sm:flex sm:items-start">
          <div class="mt-3 flex-grow text-center sm:mt-0">
            <div v-if="!offer && proposedNft">
              <h3 class="mt-2 text-3xl font-extrabold tracking-tight text-neutral-900 dark:text-neutral-50 sm:text-4xl">
                Make an Offer
              </h3>
              <OfferEditor :nft="proposedNft" :side="side" @created-offer="validateAndUploadOffer($event)" />
              <div class="relative mb-8">
                <div class="absolute inset-0 flex items-center" aria-hidden="true">
                  <div class="w-full border-t border-neutral-300 dark:border-neutral-600" />
                </div>
                <div class="relative flex justify-center">
                  <span
                    class="bg-white px-3 text-base font-semibold leading-6 text-neutral-900 dark:bg-black dark:text-neutral-50"
                    >or</span
                  >
                </div>
              </div>
            </div>
            <h3 class="mt-2 text-3xl font-extrabold tracking-tight text-neutral-900 dark:text-neutral-50 sm:text-4xl">
              Upload an Offer
            </h3>
            <div v-if="!offer || validationError" class="mt-8">
              <DropZone @drop.prevent="drop" @change="selectedFile" />
            </div>
            <div v-if="offer && nft && !validationError">
              <OfferDetails :offer="offer" :nft="nft" />
            </div>
            <div v-if="validationError || uploadError" class="mt-4 text-red-500">
              {{ validationError || uploadError }}
            </div>
          </div>
        </div>

        <div class="mt-8 lg:mt-4 lg:flex lg:flex-row-reverse">
          <button
            type="button"
            :disabled="!offer || validationError"
            :class="[
              !offer || validationError
                ? 'cursor-not-allowed bg-neutral-400 dark:bg-neutral-500'
                : 'button-hover cursor-pointer bg-black dark:bg-white',
            ]"
            class="inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm dark:text-black lg:ml-3 lg:w-auto lg:text-sm"
            @click="uploadOfferFile(dropzoneFile)"
          >
            Upload
            <!-- TODO Pending state -->
          </button>
          <button
            type="button"
            class="mt-3 inline-flex w-full justify-center rounded-md border border-neutral-300 bg-white px-4 py-2 text-base font-medium text-neutral-700 shadow-sm hover:bg-neutral-50 dark:border-neutral-600 dark:bg-black dark:text-neutral-200 dark:hover:bg-neutral-900 lg:mt-0 lg:w-auto lg:text-sm"
            @click="reset()"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
    <ClientOnly v-if="offer && nft">
      <TransitionRoot as="template" :show="open">
        <Dialog as="div" class="relative z-10" @close="reset()">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="ease-in duration-200"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <div class="bg-opacity-75 fixed inset-0 bg-neutral-500 transition-opacity dark:bg-neutral-400" />
          </TransitionChild>

          <div class="fixed inset-0 z-10 overflow-y-auto">
            <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enter-to="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leave-from="opacity-100 translate-y-0 sm:scale-100"
                leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel
                  class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all dark:bg-black sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
                >
                  <div>
                    <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                      <CheckIcon class="h-6 w-6 text-green-600" aria-hidden="true" />
                    </div>
                    <div class="mt-3 text-center sm:mt-5">
                      <DialogTitle as="h3" class="text-lg font-medium leading-6 text-neutral-900 dark:text-neutral-50">
                        Upload Successful
                      </DialogTitle>
                      <div class="mt-2">
                        <p class="text-sm text-neutral-500 dark:text-neutral-400">
                          You offer has been added to MintGarden.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <NuxtLink
                      :to="getUrlForNft(nft)"
                      class="button-hover inline-flex w-full justify-center rounded-md border border-transparent bg-black px-4 py-2 text-base font-medium text-white shadow-sm dark:bg-white dark:text-black sm:col-start-2 sm:text-sm"
                      @click="open = false"
                    >
                      Go to NFT
                    </NuxtLink>
                    <button
                      ref="cancelButtonRef"
                      type="button"
                      class="mt-3 inline-flex w-full justify-center rounded-md border border-neutral-300 bg-white px-4 py-2 text-base font-medium text-neutral-700 shadow-sm hover:bg-neutral-50 dark:border-neutral-600 dark:bg-black dark:text-neutral-200 dark:hover:bg-neutral-900 sm:col-start-1 sm:mt-0 sm:text-sm"
                      @click="reset()"
                    >
                      Reset
                    </button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </TransitionRoot>
    </ClientOnly>
  </Container>
</template>
